import { type ClassValue, clsx } from 'clsx';
import { formatDistanceToNow } from 'date-fns';
import { twMerge } from 'tailwind-merge';
import { convertToDate } from 'src/shared/lib/date.utils';
import { cleanseObject } from 'src/shared/lib/query.utils';
import { ToDateAble } from 'src/shared/types/util.types';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function square(size: number) {
  return { width: `${size}px`, height: `${size}px` };
}

export function getCssWidth(width: number | string | undefined) {
  return width !== undefined ? { width: typeof width === 'number' ? `${width}px` : width } : {};
}
export function getCssHeight(height: number | string | undefined) {
  return height !== undefined ? { height: typeof height === 'number' ? `${height}px` : height } : {};
}

export function sum(...arr: number[]) {
  return arr.reduce((final, el) => final + el, 0);
}

export function median(...arr: number[]) {
  const sorted = arr.sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[middle - 1] + sorted[middle]) / 2 : sorted[middle];
}

export function arrayFrom(length: number) {
  return Array.from({ length }, (_, i) => i + 1);
}

export function isValidEmail(input: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(input);
}

export function relativeTime(input: ToDateAble | undefined): string {
  if (!input) {
    return 'N/A';
  }
  const date = convertToDate(input);
  if (!date || isNaN(date.getTime())) {
    return 'N/A';
  }
  return formatDistanceToNow(date, { addSuffix: true });
}

export function generateInternalId(name: string): string {
  // Convert to lowercase, replace spaces with hyphens, and remove special characters
  return name
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .substring(0, 50); // Limit length
}

export function castToStrings(object: Record<string, unknown>): Record<string, string> {
  return Object.fromEntries(Object.entries(cleanseObject(object)).map(([key, value]) => [key, String(value)]));
}

export function areSetsEqual(set1: Set<string>, set2: Set<string>): boolean {
  // Check if the sets have the same size
  if (set1.size !== set2.size) {
    return false;
  }

  // Check if every element in set1 is in set2
  for (let elem of set1) {
    if (!set2.has(elem)) {
      return false;
    }
  }

  // If all checks pass, the sets are equal
  return true;
}

export function arrayEqualsSet(array: string[], set: Set<string>): boolean {
  if (array.length !== set.size) {
    return false;
  }
  for (let element of array) {
    if (!set.has(element)) {
      return false;
    }
  }
  return true;
}

// make fuzzy actually fuzzy (fuse.js)
export function fuzzyMatch(str: string = '', search: string = '') {
  if (!search) {
    return true;
  }

  const normalizedKeyword = str
    .toLowerCase()
    .replaceAll(/[^\w\s]/g, '')
    .trim();
  const normalizedSearch = search
    .toLowerCase()
    .replaceAll(/[^\w\s]/g, '')
    .trim();

  return normalizedKeyword.includes(normalizedSearch) || normalizedKeyword === normalizedSearch;
}

export function isSimpleFacet(facet: string[] | any): facet is string[] {
  return Array.isArray(facet) && (facet as unknown[]).every((tag) => typeof tag === 'string');
}

export function uniqueArray<T>(array: T[] | undefined, key?: keyof T): T[] {
  if (!array) return [];

  if (key) {
    const seen = new Set();
    return array.filter((item) => {
      const value = item[key];
      if (seen.has(value)) {
        return false;
      }
      seen.add(value);
      return true;
    });
  }

  return [...new Set(array)];
}

export function semverSort(a: string, b: string) {
  // Remove 'v' prefix if present
  let aParts = a.replace(/^v/, '').split('.');
  let bParts = b.replace(/^v/, '').split('.');

  for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
    const aNum = parseInt(aParts[i] || '0');
    const bNum = parseInt(bParts[i] || '0');

    if (aNum !== bNum) {
      return aNum - bNum;
    }
  }

  // If the versions are the same, return 0
  return 0;
}

export function uniqueElements<T extends Record<string, any>>(array: T[], key: keyof T): T[] {
  const seenIds = new Set();
  return array.filter((item) => {
    if (seenIds.has(item[key])) {
      return false;
    } else {
      seenIds.add(item[key]);
      return true;
    }
  });
}

export function safeAssert(condition: unknown, message?: string | Error) {
  if (import.meta.env.DEV) {
    if (!condition) {
      throw message instanceof Error ? message : new Error(message ?? 'Assertion failed');
    }
  }
}

export function normalizeEmail(email: string): string {
  const [localPart, domain] = email.split('@');
  if (!localPart || !domain) return email;

  const normalizedDomain = domain.toLowerCase();

  let normalizedLocalPart = localPart.toLowerCase();

  // Gmail-specific: remove dots and anything after plus
  if (normalizedDomain === 'gmail.com') {
    normalizedLocalPart = normalizedLocalPart.replace(/\./g, '');
  }

  normalizedLocalPart = normalizedLocalPart.split('+')[0];

  return `${normalizedLocalPart}@${normalizedDomain}`;
}

export function listToSentence(arr: string[], conjunction = 'and'): string {
  if (arr.length === 0) return '';
  if (arr.length === 1) return arr[0];
  const head = arr.slice(0, -1).join(', ');
  return `${head} ${conjunction} ${arr[arr.length - 1]}`;
}

/**
 * Type guard to filter out falsy values from an array
 */
export function isTruthy<T>(value: T | null | undefined | false | 0 | ''): value is T {
  return Boolean(value);
}
